<section class="social-content-files flex flex-col gap-3 border-t border-shikenso-purple-base-12 pt-5">
  <div class="flex items-center justify-between gap-3">
    <h3 class="flex items-center gap-1 text-sm font-bold min-h-5">
      <span>{{ 'components.socialContentFiles.attachments' | translate }}</span>

      @if (loading()) {
        <ngx-skeleton-loader class="h-4" [theme]="{ height: '16px', width: '20px', 'border-radius': '4px' }" />
      } @else {
        <span>({{ attachmentCount() }})</span>
      }
    </h3>

    @if (loading()) {
      <ngx-skeleton-loader class="h-5" [theme]="{ height: '20px', width: '100px', 'border-radius': '100vw' }" />
    } @else {
      <button
        mat-flat-button
        type="button"
        color="secondary"
        class="mat-micro-button !min-w-[100px] !px-4"
        [disabled]="uploading()"
        (click)="onUploadClick()"
      >
        <div class="flex items-center gap-1">
          <app-icon src="/icons/upload.svg" [alt]="'shared.upload' | translate" [size]="10" />
          <span>{{ 'shared.upload' | translate }}</span>
        </div>
      </button>
    }

    <app-file-uploader
      #fileUploader
      class="absolute h-0 w-0 overflow-hidden opacity-0"
      [imageWidth]="0"
      [imageHeight]="0"
      [title]="'components.socialContentFiles.fileUploadTitle' | translate"
      [fileForm]="uploadFileControl"
      [fileExtensions]="allowedFileExtensions"
      [clearAfterUpload]="true"
      [uploadRequest]="uploadRequest"
    />
  </div>

  <div class="flex flex-col gap-2">
    @if (loading()) {
      @for (index of listSkeletonIndexes; track index) {
        <div class="flex flex-col">
          <div class="flex min-w-0 flex-1 items-center justify-between">
            <div class="flex min-w-0 flex-1 gap-1">
              <ngx-skeleton-loader
                class="h-4 flex"
                [theme]="{ height: '16px', width: '16px', 'border-radius': '4px' }"
              />
              <ngx-skeleton-loader
                class="h-4 flex"
                [theme]="{ height: '16px', width: '132px', 'border-radius': '4px' }"
              />
            </div>

            <div class="flex items-center">
              <div class="flex h-6 w-6 items-center justify-center">
                <ngx-skeleton-loader
                  class="h-5 flex"
                  [theme]="{ height: '20px', width: '20px', 'border-radius': '100vw' }"
                />
              </div>

              <div class="flex h-6 w-6 items-center justify-center">
                <ngx-skeleton-loader
                  class="h-5 flex"
                  [theme]="{ height: '20px', width: '20px', 'border-radius': '100vw' }"
                />
              </div>
            </div>
          </div>

          <ngx-skeleton-loader class="h-3 flex" [theme]="{ height: '12px', width: '170px', 'border-radius': '4px' }" />
        </div>
      }
    } @else {
      @if (uploading()) {
        <div class="flex flex-col">
          <div class="flex min-w-0 flex-1 items-center justify-between">
            <div class="flex min-w-0 flex-1 gap-1">
              <ngx-skeleton-loader
                class="h-4 flex"
                [theme]="{ height: '16px', width: '16px', 'border-radius': '4px' }"
              />
              <ngx-skeleton-loader
                class="h-4 flex"
                [theme]="{ height: '16px', width: '132px', 'border-radius': '4px' }"
              />
            </div>

            <div class="flex items-center">
              <div class="flex h-6 w-6 items-center justify-center">
                <ngx-skeleton-loader
                  class="h-5 flex"
                  [theme]="{ height: '20px', width: '20px', 'border-radius': '100vw' }"
                />
              </div>

              <div class="flex h-6 w-6 items-center justify-center">
                <ngx-skeleton-loader
                  class="h-5 flex"
                  [theme]="{ height: '20px', width: '20px', 'border-radius': '100vw' }"
                />
              </div>
            </div>
          </div>

          <ngx-skeleton-loader class="h-3 flex" [theme]="{ height: '12px', width: '170px', 'border-radius': '4px' }" />
        </div>
      }

      @for (file of fileRows(); track file.id) {
        <div class="flex flex-col">
          <div class="flex min-w-0 flex-1 items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-1">
              <app-icon class="shrink-0" [src]="file.fileIcon" [size]="16" />
              @if (file.metadataLoading) {
                <ngx-skeleton-loader
                  class="h-4 flex"
                  [theme]="{ height: '16px', width: '132px', 'border-radius': '4px' }"
                />
              } @else {
                <span
                  class="line-clamp-1 text-ellipsis text-sm"
                  matTooltip
                  matTooltipPosition="above"
                  [appTextClampTooltip]="file.fileName"
                >
                  {{ file.fileName }}
                </span>
              }
            </div>

            <div class="flex items-center">
              @if (file.isImage) {
                <button
                  mat-icon-button
                  type="button"
                  class="mat-secondary-transparent-button-color shrink-0"
                  matTooltipPosition="above"
                  [matTooltipShowDelay]="400"
                  [matTooltip]="'components.socialContentFiles.previewFile' | translate"
                  (click)="openPreview(file)"
                >
                  <app-icon
                    src="/icons/zoom-in-area.svg"
                    [alt]="'components.socialContentFiles.previewFile' | translate"
                    [size]="14"
                  />
                </button>
              }

              <button
                mat-icon-button
                type="button"
                class="mat-secondary-transparent-button-color shrink-0"
                matTooltipPosition="above"
                [matTooltipShowDelay]="400"
                [matTooltip]="'components.socialContentFiles.downloadFile' | translate"
                [disabled]="file.metadataLoading"
                (click)="downloadFile(file)"
              >
                <app-icon
                  src="/icons/download.svg"
                  [alt]="'components.socialContentFiles.downloadFile' | translate"
                  [size]="14"
                />
              </button>

              @if (file.canDelete) {
                <button
                  mat-icon-button
                  type="button"
                  class="mat-secondary-transparent-button-color shrink-0"
                  matTooltipPosition="above"
                  [matTooltipShowDelay]="400"
                  [matTooltip]="'components.socialContentFiles.removeFile' | translate"
                  (click)="removeFile(file)"
                >
                  <app-icon
                    src="/icons/trash.svg"
                    [alt]="'components.socialContentFiles.removeFile' | translate"
                    [size]="14"
                  />
                </button>
              }
            </div>
          </div>

          <span class="truncate text-[10px] text-shikenso-purple-base-60 -mt-0.5">{{ file.uploadInfo }}</span>
        </div>
      }
    }
  </div>
</section>

@if (previewFile(); as file) {
  <div
    cdkTrapFocus
    cdkTrapFocusAutoCapture
    class="fixed inset-0 z-[1000] flex h-full w-full items-center justify-center bg-black/70 p-6 backdrop-blur-[8px]"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'components.socialContentFiles.imagePreview' | translate"
    (keydown.escape)="closePreview()"
    (click)="closePreview($event)"
  >
    <div
      class="relative grid max-h-[90vh] max-w-[90vw] grid-cols-1 grid-rows-1 place-items-center"
      (click)="$event.stopPropagation()"
    >
      <div class="absolute -right-8 -top-8 z-20">
        <button
          mat-icon-button
          type="button"
          class="mat-big-icon-button"
          [matTooltip]="'shared.close' | translate"
          (click)="closePreview($event)"
        >
          <app-icon
            src="/icons/close.svg"
            color="var(--shikenso-base-white)"
            [alt]="'shared.close' | translate"
            [size]="24"
          />
        </button>
      </div>

      @if (previewImageLoading()) {
        <ngx-skeleton-loader
          class="z-0 col-start-1 row-start-1 justify-self-center"
          [theme]="{ height: 'min(90vh, 800px)', width: 'min(90vw, 800px)', 'border-radius': '8px' }"
        />
      }

      @if (previewImageUrl()) {
        <img
          class="z-10 col-start-1 row-start-1 block h-auto max-h-[90vh] w-auto max-w-[90vw] rounded-lg object-contain"
          [class.opacity-0]="previewImageLoading()"
          [class.opacity-100]="!previewImageLoading()"
          [src]="previewImageUrl()"
          [alt]="file.fileName"
          (load)="previewImageLoading.set(false)"
          (error)="previewImageFailed.set(true); previewImageLoading.set(false)"
        />
      }

      @if (previewImageFailed()) {
        <span class="z-10 col-start-1 row-start-1 rounded-lg bg-shikenso-base-white px-4 py-3 text-sm">
          {{ 'components.socialContentFiles.previewUnavailable' | translate }}
        </span>
      }
    </div>
  </div>
}
